/**
 * Data hook for the role-specific dashboards.
 *
 * Primary source is GET /v1/dashboard/role-snapshot. Until that ships, or if it
 * is unavailable, the hook degrades to the legacy /v1/dashboard/snapshot and
 * maps what it can — so the new dashboards render real counts today and the
 * role-specific blocks (`today`, `exceptions`, `team`, `locations`) simply
 * report themselves absent rather than the page breaking.
 *
 * `today` is always org-local today and does not move with the period toggle.
 * That separation is deliberate: the operational counts a dealer opens the
 * page for must be stable while they explore the trend.
 */

import { useMemo } from 'react';
import { dashboardService } from '../../lib/services';
import { useApi } from '../../lib/hooks/useApi';

export const PERIODS = [
  { id: 'today', label: 'Today', days: 1 },
  { id: '7d',    label: '7d',    days: 7 },
  { id: '30d',   label: '30d',   days: 30 },
  { id: '90d',   label: '90d',   days: 90 },
];

export const periodDays = (id) => PERIODS.find((p) => p.id === id)?.days ?? 7;
export const periodLabel = (id) => PERIODS.find((p) => p.id === id)?.label ?? '7d';

const EMPTY_SPLIT = { new: 0, used: 0, unknown: 0, total: 0 };

/** Shape every dashboard reads. Missing blocks are null, not undefined. */
function normalise(raw, { role, days, legacy }) {
  const r = raw || {};
  const w = r.window || {};
  return {
    role: r.role || role,
    timezone: r.timezone || null,
    generatedAt: r.generatedAt || null,
    legacy,

    today: r.today || null,

    window: {
      days: w.days ?? days,
      newLeads:     { ...EMPTY_SPLIT, ...(w.newLeads || {}) },
      appointments: { booked: 0, attended: 0, noShow: 0, ...(w.appointments || {}) },
      testDrives:   { ...EMPTY_SPLIT, ...(w.testDrives || {}) },
      sold:         { ...EMPTY_SPLIT, ...(w.sold || {}) },
      
      funnel:       w.funnel || null,
      deliveries:   w.deliveries ?? 0,
      conversion:   w.conversion || null,
      // Carried across from the legacy snapshot so nothing already shown is lost.
      hotLeads:     w.hotLeads ?? r.hotLeads ?? 0,
      stalledLeads: w.stalledLeads ?? r.stalledLeads ?? 0,
      bookings:     w.bookings ?? r.bookingsInWindow ?? 0,
      voiceCalls:   w.voiceCalls ?? r.voiceCalls ?? 0,
      flaggedCalls: w.flaggedCalls ?? r.flaggedCalls ?? 0,
      totalLeads:   w.totalLeads ?? r.totalLeads ?? 0,
      totalCustomers: w.totalCustomers ?? r.totalCustomers ?? 0,
    },

    exceptions: r.exceptions
      ? { unactionedLeads: 0, leadsWithoutNextEvent: 0, unassignedLeads: 0, overdueTasks: 0, aiNurtureExpiringSoon: 0, items: [], ...r.exceptions }
      : null,

    aiVsHuman: r.aiVsHuman || null,
    locations: Array.isArray(r.locations) ? r.locations : null,
    team: Array.isArray(r.team) ? r.team : null,
    mine: r.mine || null,
    // Role-specific blocks (2026-10-01): BDC Agent's queue/SLA board and
    // the Test Drive Controller's demo-fleet board. Server fills these
    // only when the caller carries the matching role.
    bdc: r.bdc || null,
    testDriveController: r.testDriveController || null,
  };
}

/**
 * @param role         which dashboard is asking (drives server-side scoping)
 * @param period       'today' | '7d' | '30d' | '90d'
 * @param consultantId scope for the consultant view
 * @param locationIds  Set — empty means all sites
 */
export function useRoleSnapshot({ role, period = '7d', consultantId = null, locationIds = null }) {
  const days = periodDays(period);
  const locKey = locationIds && locationIds.size ? [...locationIds].sort().join(',') : '';

  const { data, error, loading, refetch } = useApi(
    async () => {
      try {
        const rs = await dashboardService.roleSnapshot({
          role, days,
          consultantId: consultantId || undefined,
          locationIds: locKey || undefined,
        });
        if (rs && typeof rs === 'object') return { raw: rs, legacy: false };
      } catch (e) {
        // 404 = endpoint not deployed yet; anything else we also try to
        // survive, since the legacy snapshot is strictly more available.
        const status = e?.response?.status;
        if (status && status !== 404 && status !== 501 && status < 500) throw e;
      }
      const legacy = await dashboardService.snapshot({ days });
      return { raw: legacy, legacy: true };
    },
    { fallback: null, deps: [role, days, consultantId, locKey] },
  );

  const snapshot = useMemo(
    () => (data ? normalise(data.raw, { role, days, legacy: !!data.legacy }) : null),
    [data, role, days],
  );

  return { snapshot, error, loading, refetch, days };
}
